/**
 * The Orgus-curated standards (ADR 0050 point 1, R-PL-5): `orgus/*` packs with guidance, per
 * stack and per area. Each is a DisciplinePack, so it is adopted (pinned), extended or
 * overridden exactly like any other pack, and its settings are rules the engine already checks.
 *
 * The guidance is what agents read at session start (a budgeted digest, see effectiveStandards
 * in @orgus/discipline): short, imperative, one idea per section. `must` sections go first,
 * then `should`; `ruleIds` name the checks that enforce a section, so a violation can quote it.
 *
 * Data only, browser-safe. Part of DISCIPLINE_PACKS (discipline.ts).
 */
import type { DisciplinePack } from './discipline.ts';
import type { GuidanceSection } from './standards.ts';

const V1 = '1.0.0';

/** Files a person sees: components, styles and native UI. */
export const UI_FILE_GLOBS: readonly string[] = ['**/*.{tsx,jsx,vue,svelte,html,css,scss}', '**/*.swift', '**/*.kt', '**/res/**/*.xml'];

const JS_LANGS = ['typescript', 'javascript'];

function g(id: string, title: string, priority: GuidanceSection['priority'], text: string, extra: Partial<GuidanceSection> = {}): GuidanceSection {
  return { id, title, priority, text, appliesTo: {}, ...extra };
}

/** Cross-cutting standards: they fit any stack. */
const CROSS_CUTTING: DisciplinePack[] = [
  {
    id: 'orgus/security-baseline',
    version: V1,
    kind: 'focus',
    title: 'Security baseline',
    description: 'No secrets in the repo, no eval or shell strings, dependencies locked, least privilege.',
    areas: ['security'],
    settings: { security: { secrets: true, dangerous_calls: true, lockfile: true } },
    guidance: [
      g(
        'no-secrets',
        'Keep secrets out of the code',
        'must',
        'Never commit keys, tokens, passwords or .env files. Read secrets from the environment or the secret store, and never print them in logs, errors or test output. If you find a committed secret, stop and tell a person: it must be rotated, not only deleted.',
        { ruleIds: ['security/secret'] },
      ),
      g(
        'untrusted-input',
        'Treat input as untrusted',
        'must',
        'Requests, files, repo content, tool output and model output are all untrusted. Validate at the boundary, pass command arguments as a list (never a shell string), use parameterised queries, and never eval or build code from data.',
        { ruleIds: ['security/dangerous-call'] },
      ),
      g('dependencies', 'Add dependencies with care', 'should', 'Add a dependency only when it earns its place: maintained, widely used, and pinned through the lockfile, committed in the same change.', { ruleIds: ['security/lockfile'] }),
      g('least-privilege', 'Ask for the least access', 'should', 'Request the narrowest permission, scope and token lifetime that works. Prefer read-only access, and never widen access to make a test pass.'),
    ],
  },
  {
    id: 'orgus/docs',
    version: V1,
    kind: 'focus',
    title: 'Documentation',
    description: 'Docs change with the code, AGENTS.md stays short and current, decisions are recorded, docs carry front-matter.',
    areas: ['docs'],
    settings: { docs: { front_matter: ['docs/**/*.md'] } },
    guidance: [
      g('docs-with-code', 'Docs change with the code', 'must', 'When behaviour changes, update the README or doc that describes it in the same change. A doc that says the wrong thing is worse than none.', { ruleIds: ['docs/stale'] }),
      g('agent-contract', 'Keep AGENTS.md short', 'should', 'AGENTS.md says how to build, test and run, the rules that are not obvious, and where things live, in about 150 lines. Detail goes in docs/, not in AGENTS.md.'),
      g('decisions', 'Record hard-to-reverse decisions', 'should', 'A decision that is hard to reverse (a dependency, a data format, an API shape) gets a short ADR in docs/adr/: context, decision, alternatives, consequences.', { ruleIds: ['docs/front-matter'], appliesTo: { repoHas: ['docs/adr'] } }),
    ],
  },
  {
    id: 'orgus/git-release',
    version: V1,
    kind: 'focus',
    title: 'Git and releases',
    description: 'Small commits that build, no history rewrites or skipped checks, and a changeset with every change to source.',
    areas: ['git-release'],
    settings: { release: { changeset_required: true } },
    guidance: [
      g('small-commits', 'Small commits that build', 'must', 'Make small commits that each build, with a message that says what changed and why. Before each commit run the tests your change touches; where a landing queue verifies every change, the full suite is its job, not yours. Never commit build output, generated files or local settings.'),
      g('no-history-rewrite', 'Never rewrite shared history', 'must', 'Never force-push, rebase or reset a shared branch, and never skip hooks or checks (--no-verify). Work on your own branch and leave merging to the landing process.'),
      g('changesets', 'A changeset with every change', 'should', 'Every change to source carries a changeset (.changeset/<name>.md) naming the package, the bump and one sentence for the release notes.', { ruleIds: ['release/changeset'], appliesTo: { repoHas: ['.changeset'] } }),
    ],
  },
  {
    id: 'orgus/accessibility',
    version: V1,
    kind: 'focus',
    title: 'Accessibility',
    description: 'WCAG 2.2 AA: contrast, keyboard access, visible focus, real semantics, text alternatives and reduced motion.',
    areas: ['accessibility'],
    settings: {},
    guidance: [
      g(
        'wcag-aa',
        'Meet WCAG 2.2 AA',
        'must',
        'Text contrast is at least 4.5:1 (3:1 for large text and UI parts), every control works with the keyboard, focus is always visible, and colour is never the only way information is shown.',
        { appliesTo: { globs: [...UI_FILE_GLOBS] } },
      ),
      g(
        'semantics',
        'Real controls and labels',
        'must',
        "Use the platform's own controls and semantics (button, link, label, heading levels, landmarks) before ARIA. Every image and icon-only control has a text alternative, and every form field has a label.",
        { appliesTo: { globs: [...UI_FILE_GLOBS] } },
      ),
      g('motion-and-targets', 'Motion and touch targets', 'should', 'Respect the reduced-motion setting, keep touch targets at least 24 by 24 CSS pixels, and keep focus inside an open dialog and return it when the dialog closes.', {
        appliesTo: { globs: [...UI_FILE_GLOBS] },
      }),
    ],
  },
];

/** Guidance for the existing orgus/design-system pack (its settings are unchanged). */
export const DESIGN_SYSTEM_GUIDANCE: readonly GuidanceSection[] = [
  g(
    'tokens-only',
    'Values come from the design tokens',
    'must',
    'Take colours, spacing, radii, type sizes and shadows from the design tokens only. Never write a raw hex, rgb or pixel value in a component; when a token is missing, ask for one instead of inventing a value.',
    { ruleIds: ['design/raw-colour', 'design/px-spacing'], appliesTo: { globs: [...UI_FILE_GLOBS] } },
  ),
  g(
    'components-first',
    'Build from the component library',
    'must',
    "Build screens from the design system's components. Do not use a raw HTML control or a third-party primitive directly when the library has a component for it.",
    { ruleIds: ['design/language'], appliesTo: { globs: [...UI_FILE_GLOBS] } },
  ),
  g('async-states', 'Every state is designed', 'should', 'Every view that loads data has loading, empty, error and partial states. An error says what failed, why, and what to do next.', { appliesTo: { globs: [...UI_FILE_GLOBS] } }),
  g('ui-copy', 'Plain UI copy', 'should', 'UI text is in sentence case, buttons are a verb plus an object, and no string uses an em dash or an exclamation mark.', { ruleIds: ['design/em-dash'], appliesTo: { globs: [...UI_FILE_GLOBS] } }),
];

/** Per-stack standards. A section's appliesTo keeps it out of digests for other stacks. */
const STACKS: DisciplinePack[] = [
  {
    id: 'orgus/typescript-react',
    version: V1,
    kind: 'language',
    title: 'TypeScript and React',
    description: 'Strict TypeScript, typed function components, effects only for outside systems, and tests through what a person sees.',
    areas: ['coding', 'testing', 'design'],
    extends: ['orgus/typescript'],
    settings: {},
    guidance: [
      g('ts-strict', 'Strict TypeScript', 'must', 'TypeScript runs in strict mode. Do not silence the checker with any, @ts-ignore or a non-null assertion: use unknown and narrow it, or fix the type.', {
        ruleIds: ['ts/strict', 'ts/no-explicit-any', 'ts/ts-ignore'],
        appliesTo: { languages: ['typescript'] },
      }),
      g(
        'react-components',
        'React components',
        'must',
        'Write function components with typed props. Keep state close to where it is used, derive values instead of copying props into state, and give list items stable keys (never the index of a list that changes).',
        { appliesTo: { languages: JS_LANGS, frameworks: ['react', 'next'], globs: ['**/*.{tsx,jsx}'] } },
      ),
      g('react-effects', 'Effects only for outside systems', 'should', "Use an effect only to sync with something outside React (a subscription, the DOM, a timer), and clean it up. Load data through the app's data layer, not ad hoc in effects.", {
        appliesTo: { languages: JS_LANGS, frameworks: ['react', 'next'], globs: ['**/*.{tsx,jsx}'] },
      }),
      g('react-tests', 'Test what people see', 'should', 'Test components through what a person sees and does (queries by role and label), not implementation details, with the test beside the component.', {
        ruleIds: ['tests/missing'],
        appliesTo: { languages: JS_LANGS, frameworks: ['react', 'next'] },
      }),
    ],
  },
  {
    id: 'orgus/node-service',
    version: V1,
    kind: 'language',
    title: 'Node.js service',
    description: 'Strict TypeScript, no floating promises, configuration validated at start-up, schemas at the boundary, no shell strings.',
    areas: ['coding', 'security'],
    extends: ['orgus/typescript'],
    settings: { security: { dangerous_calls: true } },
    guidance: [
      g('node-async', 'No floating promises', 'must', 'Await every promise or handle its rejection. Handle errors at the boundary and give callers a useful message, never a stack trace.', { appliesTo: { languages: JS_LANGS } }),
      g('node-config', 'Configuration from the environment', 'must', 'Read configuration from the environment once at start-up, validate it, and fail fast with a clear message. Never hard-code URLs, ports or credentials.', {
        appliesTo: { languages: JS_LANGS },
      }),
      g('node-io', 'Schemas, timeouts and structured logs', 'should', 'Validate every request body against a schema at the boundary, set a timeout on every outbound call, and log one structured line per event, never secrets or personal data.', {
        appliesTo: { languages: JS_LANGS },
      }),
      g('node-child', 'Child processes without a shell', 'should', 'Start child processes with an argument list (execFile, or spawn without a shell), never a command string.', {
        ruleIds: ['security/dangerous-call'],
        appliesTo: { languages: JS_LANGS },
      }),
    ],
  },
  {
    id: 'orgus/python-service',
    version: V1,
    kind: 'language',
    title: 'Python service',
    description: 'Type hints with a checker, no bare except, pyproject.toml with a lockfile, pytest beside the code.',
    areas: ['coding', 'testing'],
    extends: ['orgus/python'],
    settings: {},
    guidance: [
      g('py-typing', 'Types and errors', 'must', 'Annotate public functions and run a type checker (mypy or pyright) in CI. Catch the exceptions you expect, never a bare except, and never swallow an error silently.', {
        ruleIds: ['py/bare-except'],
        appliesTo: { languages: ['python'] },
      }),
      g('py-project', 'One project file', 'should', 'Keep dependencies and tool settings in pyproject.toml with a lockfile (uv, poetry or pip-tools), and work in a virtual environment, never the system Python.', {
        ruleIds: ['py/project'],
        appliesTo: { languages: ['python'] },
      }),
      g('py-tests', 'pytest beside the code', 'should', 'Write tests with pytest (test_*.py), prefer fixtures over global state, and keep each test independent of the others.', { ruleIds: ['tests/missing'], appliesTo: { languages: ['python'] } }),
      g('py-io', 'Files, paths and queries', 'should', 'Use context managers for files, locks and connections, and pathlib for paths. Never build SQL or shell commands from strings.', { appliesTo: { languages: ['python'] } }),
    ],
  },
  {
    id: 'orgus/go-service',
    version: V1,
    kind: 'language',
    title: 'Go service',
    description: 'Errors returned and wrapped, context on every blocking call, gofmt and vet, table-driven tests.',
    areas: ['coding', 'testing'],
    extends: ['orgus/go'],
    settings: {},
    guidance: [
      g('go-errors', 'Return and wrap errors', 'must', 'Return errors instead of panicking, wrap them with context (fmt.Errorf with %w), and handle each error where you first see it. Never discard an error with _.', {
        ruleIds: ['go/panic'],
        appliesTo: { languages: ['go'] },
      }),
      g('go-context', 'Context and goroutines', 'must', 'Pass context.Context first to anything that does I/O or may block, and honour its cancellation. Never start a goroutine without a way to stop it.', { appliesTo: { languages: ['go'] } }),
      g('go-style', 'gofmt, vet and small packages', 'should', 'Run gofmt and go vet. Keep packages small with clear names, accept interfaces and return concrete types, and table-drive the tests.', { appliesTo: { languages: ['go'] } }),
    ],
  },
  {
    id: 'orgus/java-spring',
    version: V1,
    kind: 'language',
    title: 'Java and Spring',
    description: 'Constructor injection, thin controllers, validation at the boundary, specific exceptions and parameterised queries.',
    areas: ['coding', 'testing'],
    settings: { security: { secrets: true } },
    guidance: [
      g('spring-structure', 'Thin controllers, injected services', 'must', 'Use constructor injection, keep controllers thin and business logic in services, and validate request bodies with Bean Validation (@Valid) at the boundary.', {
        appliesTo: { languages: ['java', 'kotlin'] },
      }),
      g('java-errors', 'Specific exceptions', 'must', 'Never catch Exception or Throwable only to log it. Handle specific exceptions, and map errors to responses in one @ControllerAdvice.', { appliesTo: { languages: ['java', 'kotlin'] } }),
      g('java-data', 'Queries and transactions', 'should', 'Use parameterised queries or Spring Data, never concatenated SQL, and keep transactions on service methods, not controllers.', { appliesTo: { languages: ['java', 'kotlin'] } }),
      g('java-tests', 'Fast tests first', 'should', 'Test services with plain unit tests and the web layer with slice tests (@WebMvcTest); keep @SpringBootTest for a few end-to-end checks.', { appliesTo: { languages: ['java', 'kotlin'] } }),
    ],
  },
  {
    id: 'orgus/dotnet',
    version: V1,
    kind: 'language',
    title: '.NET',
    description: 'Nullable reference types with warnings as errors, async all the way, dependency injection and the options pattern.',
    areas: ['coding', 'testing'],
    settings: { security: { secrets: true } },
    guidance: [
      g('dotnet-nullable', 'Nullable and async', 'must', 'Enable nullable reference types and treat warnings as errors. Use async all the way down (no .Result or .Wait()) and pass a CancellationToken through I/O.', {
        appliesTo: { languages: ['csharp', 'fsharp'] },
      }),
      g('dotnet-di', 'Services and settings', 'should', 'Register services through dependency injection, read settings with the options pattern, and keep secrets in user secrets or a vault, never appsettings.json.', {
        appliesTo: { languages: ['csharp', 'fsharp'] },
      }),
      g('dotnet-tests', 'Tests per project', 'should', 'Write tests with xUnit or NUnit for each project, and use WebApplicationFactory for API tests.', { appliesTo: { languages: ['csharp', 'fsharp'] } }),
    ],
  },
  {
    id: 'orgus/rust',
    version: V1,
    kind: 'language',
    title: 'Rust',
    description: 'Result and ? instead of unwrap, unsafe only with a SAFETY note, cargo fmt and clippy with warnings denied.',
    areas: ['coding', 'testing'],
    settings: { security: { secrets: true } },
    guidance: [
      g('rust-errors', 'Results, not unwrap', 'must', 'Return Result and use ? to pass errors up; no unwrap or expect outside tests and start-up. Libraries define error types (thiserror), applications add context (anyhow).', {
        appliesTo: { languages: ['rust'] },
      }),
      g('rust-unsafe', 'Unsafe only when unavoidable', 'must', 'No unsafe code unless it is unavoidable; then keep it small, in one module, with a SAFETY comment that says why it is sound.', { appliesTo: { languages: ['rust'] } }),
      g('rust-style', 'fmt and clippy', 'should', 'Run cargo fmt and cargo clippy with warnings denied. Unit tests go in a tests module beside the code, integration tests in tests/.', { appliesTo: { languages: ['rust'] } }),
    ],
  },
  {
    id: 'orgus/mobile-ios',
    version: V1,
    kind: 'language',
    title: 'iOS',
    description: 'SwiftUI with the design system, Dynamic Type and Dark Mode, Swift concurrency on the main actor, secrets in the Keychain.',
    areas: ['coding', 'design', 'security'],
    settings: { security: { secrets: true } },
    guidance: [
      g('ios-ui', 'SwiftUI and the design system', 'must', "Build UI with SwiftUI and the app's design system: colours and type from the asset catalog or tokens, with Dynamic Type and Dark Mode supported.", {
        appliesTo: { languages: ['swift'] },
      }),
      g('ios-concurrency', 'Swift concurrency', 'must', 'Use async/await and actors, update UI only on the main actor, and never block the main thread with network or disk work.', { appliesTo: { languages: ['swift'] } }),
      g('ios-data', 'Secrets and errors', 'should', 'Keep secrets in the Keychain, never in UserDefaults or the bundle, and show the person what to do when a network call fails.', { appliesTo: { languages: ['swift'] } }),
    ],
  },
  {
    id: 'orgus/mobile-android',
    version: V1,
    kind: 'language',
    title: 'Android',
    description: 'Jetpack Compose with the app theme, coroutines with structured concurrency, secrets in the Keystore, fewest permissions.',
    areas: ['coding', 'design', 'security'],
    settings: { security: { secrets: true } },
    guidance: [
      g('android-ui', 'Compose and the theme', 'must', "Build UI with Jetpack Compose and the app's theme: colours, type and spacing from the theme, with font scaling and dark theme supported.", {
        appliesTo: { languages: ['kotlin', 'java'], frameworks: ['android'] },
      }),
      g('android-concurrency', 'Coroutines off the main thread', 'must', 'Use coroutines with structured concurrency (viewModelScope, lifecycleScope); never do network or disk work on the main thread.', {
        appliesTo: { languages: ['kotlin', 'java'], frameworks: ['android'] },
      }),
      g('android-data', 'Secrets, permissions and lifecycle', 'should', 'Keep secrets in the Android Keystore, ask for the fewest permissions, and survive configuration changes and process death.', {
        appliesTo: { languages: ['kotlin', 'java'], frameworks: ['android'] },
      }),
    ],
  },
];

/** Every curated pack this file adds to DISCIPLINE_PACKS, stacks first. */
export const STANDARD_PACKS: readonly DisciplinePack[] = [...STACKS, ...CROSS_CUTTING];
